<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useKioskApi } from '../composables/useKioskApi'
import { useOfflineQueue, isNetworkError } from '../composables/useOfflineQueue'
import { nowApiDateTime } from '@yuksalish/ui'
import { ArrowLeft, CircleCheck, CircleX, Keyboard, Moon, Pause, Play, RotateCcw, Sun, Timer, X } from 'lucide-vue-next'
import type { EquipmentItem, KioskEmployee, KioskLot, KioskOrder, KioskStage, PieceRate, WorkerSession } from '../types/kiosk'
import NumericKeypadModal from '../components/NumericKeypadModal.vue'

const { t, locale } = useI18n()
const WORKER_KEY = 'yuksalish.kiosk.worker_id'
const SESSION_TIMEOUT_SECONDS = 120
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const

function readStoredWorker(): number | null {
  try {
    const value = localStorage.getItem(WORKER_KEY)
    return value ? Number(value) : null
  } catch {
    return null
  }
}

function storeWorker(id: number | null): void {
  try {
    if (id === null) localStorage.removeItem(WORKER_KEY)
    else localStorage.setItem(WORKER_KEY, String(id))
  } catch {
    // worker still applies for this session
  }
}
const {
  listActiveOrders,
  listStages,
  listLots,
  recordProgress,
  closeStage,
  confirmBasketStage,
  listDowntimeReasons,
  listEquipment,
  openDowntime,
  listProductionEmployees,
  workerLogin,
  startSession,
  pauseSession,
  resumeSession,
  finishSession,
  fetchPieceRates,
  fetchBomPieceRates,
} = useKioskApi()

const PENDING_KEY = 'yuksalish.kiosk.pending_actions'

const {
  pendingCount,
  isOnline,
  flushing: syncing,
  enqueue,
  remove: removeQueued,
  flush: flushQueue,
} = useOfflineQueue({
  storageKey: PENDING_KEY,
  isRetryable: isNetworkError,
  execute: async (action) => {
    if (action.type === 'start') {
      const session = await startSession(action.payload as Parameters<typeof startSession>[0])
      activeSession.value = session
      return
    }
    if (action.type === 'pause') {
      const session = await pauseSession(
        Number(action.payload.sessionId),
        {
          paused_at: String(action.payload.paused_at),
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
      return
    }
    if (action.type === 'resume') {
      const session = await resumeSession(
        Number(action.payload.sessionId),
        {
          resumed_at: String(action.payload.resumed_at),
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
      return
    }
    if (action.type === 'finish') {
      const session = await finishSession(
        Number(action.payload.sessionId),
        {
          finished_at: String(action.payload.finished_at),
          completed_delta: Number(action.payload.completed_delta) || 0,
          rejected_delta: Number(action.payload.rejected_delta) || 0,
          rework_delta: Number(action.payload.rework_delta) || 0,
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
    }
  },
})

const pieceRates = ref<PieceRate[]>([])
const estimatedEarnings = ref<number | null>(null)
const estimatedCurrency = ref('')
/** Active shift for rate lookup + session start. Defaults from worker-login. */
const currentShift = ref<'day' | 'night'>('day')

const workers = ref<KioskEmployee[]>([])
const orders = ref<KioskOrder[]>([])
const stages = ref<KioskStage[]>([])
const lots = ref<KioskLot[]>([])

const selectedWorkerId = ref<number | null>(readStoredWorker())
const selectedOrderId = ref<number | null>(null)
const selectedLot = ref<KioskLot | null>(null)
const activeSession = ref<WorkerSession | null>(null)

const scan = ref('')
const scanError = ref('')
// On-screen number pad modals (touch terminals without a keyboard).
const showKeypad = ref(false)
const showCodeKeypad = ref(false)
const output = reactive({ completed: 0, rejected: 0, rework: 0 })
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showWorkerPicker = ref(false)
const loginCard = ref('')
const loginWorkerCode = ref('')
const loginError = ref('')
const cardInput = ref<HTMLInputElement | null>(null)
const scanInput = ref<HTMLInputElement | null>(null)
const showDefectModal = ref(false)
const showLegacyBoard = false
/** Defect / rework units entered per basket lot (submitted with completion). */
const defectEntry = reactive<Record<number, { rejected: number; rework: number }>>({})
/** Idempotency key kept until the current basket confirmation succeeds. */
const confirmEventId = ref<string | null>(null)

const showDowntime = ref(false)
const downtimeReasons = ref<Array<{ code: string; name: string }>>([])
const equipment = ref<EquipmentItem[]>([])
const downtimeForm = reactive({ reason_code: '', notes: '', machine_id: null as number | null })

const loading = ref(true)
const error = ref('')
let previousTheme: string | null = null
const now = ref(new Date())
const sessionSecondsLeft = ref(SESSION_TIMEOUT_SECONDS)
let clockTimer: number | null = null

const clockLabel = computed(() => new Intl.DateTimeFormat(locale.value, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
}).format(now.value))
const dateLabel = computed(() => new Intl.DateTimeFormat(locale.value, {
  weekday: 'short',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
}).format(now.value))
const sessionCountdown = computed(() => {
  const minutes = Math.floor(sessionSecondsLeft.value / 60)
  const seconds = String(sessionSecondsLeft.value % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

function resetSessionTimeout(): void {
  if (selectedWorkerId.value && selectedLot.value) {
    sessionSecondsLeft.value = SESSION_TIMEOUT_SECONDS
  }
}

function setLanguage(code: 'en' | 'uz' | 'ru'): void {
  locale.value = code
  document.documentElement.lang = code
  try {
    localStorage.setItem('yuksalish.locale', code)
  } catch {
    // The language still applies for the active terminal session.
  }
  resetSessionTimeout()
}

const selectedWorker = computed(() =>
  workers.value.find((w) => w.id === selectedWorkerId.value) ?? null,
)
const activeLots = computed(() => lots.value.filter((l) => l.status === 'active'))

interface ProductLine {
  key: string
  lotId: number
  product: string
  color: string
  size: string
  quantity: number
  /** Entered defect / rework units of the owning lot (shown on its first row). */
  rejected: number
  rework: number
  defectsLead: boolean
}

const selectedBasketLots = computed(() => {
  const selected = selectedLot.value
  if (!selected) return []
  if (selected.production_basket_id != null) {
    return lots.value.filter((lot) => lot.production_basket_id === selected.production_basket_id)
  }
  const basketCode = selected.basket_number ?? selected.basket_code
  if (basketCode) {
    return lots.value.filter((lot) =>
      lot.production_order_id === selected.production_order_id &&
      (lot.basket_number === basketCode || lot.basket_code === basketCode),
    )
  }
  return [selected]
})

const activeBasketLots = computed(() =>
  selectedBasketLots.value.filter((lot) => lot.status === 'active'),
)

function colorSummaryRows(summary: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(summary)) {
    return summary.flatMap((item) => item && typeof item === 'object'
      ? [item as Record<string, unknown>]
      : [])
  }
  if (!summary || typeof summary !== 'object') return []
  const record = summary as Record<string, unknown>
  for (const key of ['lines', 'items', 'colors']) {
    if (Array.isArray(record[key])) return colorSummaryRows(record[key])
  }
  if ('color_name' in record || 'color' in record || 'size_label' in record || 'size' in record) {
    return [record]
  }
  return Object.entries(record).map(([color, quantity]) => ({ color, quantity }))
}

const productLines = computed<ProductLine[]>(() => {
  return selectedBasketLots.value.flatMap((lot) => {
    const order = orders.value.find((item) => item.id === lot.production_order_id)
    const rows = colorSummaryRows(lot.color_summary)
    const defects = defectEntry[lot.id] ?? { rejected: 0, rework: 0 }
    if (rows.length) {
      return rows.map((row, index) => {
      const color = String(row.color_name ?? row.color ?? row.name ?? '')
      const size = String(row.size_label ?? row.size ?? '')
        const fallbackQuantity = rows.length === 1 ? lot.current_quantity : 0
        const quantity = Number(row.quantity ?? row.qty ?? row.current_quantity ?? fallbackQuantity)
        return {
        key: `${lot.id}:${String(row.id ?? index)}`,
        lotId: lot.id,
        product: String(row.product_name ?? order?.product_name ?? lot.product_name ?? ''),
        color,
        size,
        quantity: Number.isFinite(quantity) ? quantity : 0,
        rejected: defects.rejected,
        rework: defects.rework,
        defectsLead: index === 0,
        }
      })
    }
    return [{
      key: `${lot.id}:0`,
      lotId: lot.id,
      product: lot.product_name ?? order?.product_name ?? '',
      color: lot.color_variant ?? parseColorVariant(lot.color_summary),
      size: '',
      quantity: Number(lot.current_quantity) || 0,
      rejected: defects.rejected,
      rework: defects.rework,
      defectsLead: true,
    }]
  })
})

const defectEntryFor = (lotId: number) =>
  defectEntry[lotId] ?? { rejected: 0, rework: 0 }
const basketTotalQuantity = computed(() =>
  productLines.value.reduce((total, line) => total + line.quantity, 0),
)

/** Board filter: a production order id, or null for "all orders". */
const orderFilterId = ref<number | null>(null)

const visibleLots = computed(() =>
  orderFilterId.value === null
    ? activeLots.value
    : activeLots.value.filter((lot) => lot.production_order_id === orderFilterId.value),
)

/** Every planned / in-progress order plus its active-lot count. */
const orderCards = computed(() => {
  const counts = new Map<number, number>()
  for (const lot of activeLots.value) {
    counts.set(lot.production_order_id, (counts.get(lot.production_order_id) ?? 0) + 1)
  }
  return orders.value.map((order) => ({ order, lotCount: counts.get(order.id) ?? 0 }))
})

function orderStatusLabel(status: KioskOrder['status']): string {
  return status === 'planned' ? t('kiosk.session.planned') : t('kiosk.session.inProgress')
}

function toggleOrderFilter(id: number): void {
  orderFilterId.value = orderFilterId.value === id ? null : id
}

function stageName(code: string): string {
  return stages.value.find((s) => s.stage_code === code)?.stage_name ?? code
}

function workerLabel(worker: KioskEmployee): string {
  return `${worker.first_name} ${worker.last_name}`.trim()
}

/** Localized downtime reason label, falling back to the backend name. */
function reasonLabel(code: string, fallback = ''): string {
  const key = `kiosk.session.reasons.${code}`
  const value = t(key)
  return value === key ? fallback || code : value
}

async function loadWorkers() {
  try {
    workers.value = await listProductionEmployees()
  } catch {
    workers.value = []
  }
}

async function loadEquipment() {
  try {
    equipment.value = await listEquipment()
  } catch {
    equipment.value = []
  }
}

/** Best-effort extraction of a color-variant label from the lot's
 *  `color_summary` payload (shape varies: string | array | object). */
function parseColorVariant(summary: unknown): string {
  if (summary == null) return ''
  if (typeof summary === 'string') return summary.trim()
  if (Array.isArray(summary)) {
    return summary
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>
          return record.name ?? record.color_name ?? record.color ?? record.label ?? ''
        }
        return ''
      })
      .filter((value) => typeof value === 'string' && value.trim())
      .join(', ')
  }
  if (typeof summary === 'object') {
    const record = summary as Record<string, unknown>
    if (Array.isArray(record.colors)) return parseColorVariant(record.colors)
    if (Array.isArray(record.items)) return parseColorVariant(record.items)
    const direct = record.name ?? record.color_name ?? record.color ?? record.label
    if (typeof direct === 'string' && direct.trim()) return direct.trim()
  }
  return ''
}

/** Attaches order context (order code, product) + parsed color variant to a
 *  backend lot so every card on the universal board is self-contained. */
function enrichLot(lot: KioskLot, order: KioskOrder): KioskLot {
  return {
    ...lot,
    order_code: order.mo_code,
    product_name: order.product_name,
    color_variant: parseColorVariant(lot.color_summary) || undefined,
  }
}

/** Fetches lots + stages for every active order and flattens them into a
 *  single universal board. Keeps the selected lot object in sync. */
async function fetchBoard(): Promise<boolean> {
  if (!orders.value.length) {
    lots.value = []
    stages.value = []
    return true
  }
  try {
    const results = await Promise.all(
      orders.value.map(async (order) => {
        const [orderLots, orderStages] = await Promise.all([
          listLots(order.id),
          listStages(order.id),
        ])
        return { order, lots: orderLots, stages: orderStages }
      }),
    )
    lots.value = results.flatMap(({ order, lots: orderLots }) =>
      orderLots.map((lot) => enrichLot(lot, order)),
    )
    stages.value = results.flatMap(({ stages }) => stages)
    const current = selectedLot.value
    if (current) {
      const updated = lots.value.find((l) => l.id === current.id)
      if (updated) selectedLot.value = updated
    }
    return true
  } catch {
    return false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    orders.value = await listActiveOrders()
    const ok = await fetchBoard()
    if (!ok) error.value = t('kiosk.session.error')
    if (!selectedOrderId.value && orders.value.length > 0) {
      selectedOrderId.value = orders.value[0].id
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadDowntimeReasons() {
  try {
    const reasons = await listDowntimeReasons()
    downtimeReasons.value = reasons
  } catch {
    downtimeReasons.value = []
  }
}

onMounted(() => {
  previousTheme = document.documentElement.getAttribute('data-theme')
  document.documentElement.setAttribute('data-theme', 'dark')
  document.body.classList.add('worker-kiosk-mode')
  clockTimer = window.setInterval(() => {
    now.value = new Date()
    if (!selectedWorkerId.value || !selectedLot.value) {
      sessionSecondsLeft.value = SESSION_TIMEOUT_SECONDS
      return
    }
    sessionSecondsLeft.value = Math.max(0, sessionSecondsLeft.value - 1)
    if (sessionSecondsLeft.value === 0) clearSelection()
  }, 1000)
  for (const eventName of ACTIVITY_EVENTS) {
    window.addEventListener(eventName, resetSessionTimeout, { passive: true })
  }
  void loadWorkers()
  void loadDowntimeReasons()
  void loadEquipment()
  void load()
  void flushQueue()
})

onUnmounted(() => {
  document.body.classList.remove('worker-kiosk-mode')
  if (previousTheme) document.documentElement.setAttribute('data-theme', previousTheme)
  else document.documentElement.removeAttribute('data-theme')
  if (clockTimer !== null) window.clearInterval(clockTimer)
  for (const eventName of ACTIVITY_EVENTS) {
    window.removeEventListener(eventName, resetSessionTimeout)
  }
})

function pickWorker() {
  showWorkerPicker.value = true
  loginCard.value = ''
  loginWorkerCode.value = ''
  loginError.value = ''
  void nextTick(() => cardInput.value?.focus())
}

function selectWorker(id: number) {
  selectedWorkerId.value = id
  activeSession.value = null
  orderFilterId.value = null
  storeWorker(id)
  showWorkerPicker.value = false
}

async function submitWorkerLogin() {
  loginError.value = ''
  const cardUid = loginCard.value.trim()
  const workerCode = loginWorkerCode.value.trim()
  if (!cardUid && !workerCode) {
    loginError.value = t('kiosk.session.selectWorker')
    return
  }

  if (!cardUid && workerCode) {
    if (workers.value.length === 0) await loadWorkers()
    let worker = workers.value.find(
      (item) => item.employee_code.toLowerCase() === workerCode.toLowerCase(),
    )
    // The on-screen pad only types digits, so accept a unique numeric suffix
    // of an employee code ("88" → EMP-088) when no exact code matches.
    if (!worker && /^\d+$/.test(workerCode)) {
      const suffixMatches = workers.value.filter((item) =>
        item.employee_code.replace(/\D/g, '').endsWith(workerCode),
      )
      if (suffixMatches.length === 1) worker = suffixMatches[0]
    }
    if (!worker) {
      loginError.value = t('kiosk.session.workerCodeNotFound')
      return
    }
    selectedWorkerId.value = worker.id
    activeSession.value = null
    storeWorker(worker.id)
    if (selectedLot.value && !(await startSessionForWorker(worker.id, selectedLot.value))) {
      loginError.value = actionError.value
      return
    }
    showWorkerPicker.value = false
    return
  }

  submitting.value = true
  try {
    const response = await workerLogin({
      card_uid: cardUid,
    })
    selectedWorkerId.value = response.employee.id
    orderFilterId.value = null
    storeWorker(response.employee.id)
    currentShift.value = response.shift === 'night' ? 'night' : 'day'
    // ensure the logged-in worker is present in the picker list
    if (!workers.value.some((w) => w.id === response.employee.id)) {
      workers.value.unshift({
        id: response.employee.id,
        employee_code: response.employee.employee_code,
        first_name: response.employee.name,
        last_name: '',
        photo_url: response.employee.photo_url,
      })
    } else {
      workers.value = workers.value.map((worker) => worker.id === response.employee.id
        ? {
            ...worker,
            employee_code: response.employee.employee_code,
            first_name: response.employee.name,
            last_name: '',
            photo_url: response.employee.photo_url,
          }
        : worker)
    }
    // Step 1 — swipe while a lot is selected binds the worker and starts the
    // session (non-destructive). Re-swipes are no-ops; an active session on a
    // different lot asks before switching.
    if (response.active_session) {
      const sameLot =
        !!selectedLot.value &&
        selectedLot.value.lot_number === response.active_session.lot_number
      if (sameLot) {
        activeSession.value = response.active_session
        actionMessage.value = t('kiosk.session.activeOnLot', {
          lot: response.active_session.lot_number,
        })
      } else if (window.confirm(t('kiosk.session.switchToActiveLot', { lot: response.active_session.lot_number }))) {
        activeSession.value = response.active_session
        await restoreActiveSessionContext(response.active_session)
        actionMessage.value = t('kiosk.session.activeOnLot', {
          lot: response.active_session.lot_number,
        })
      } else {
        actionMessage.value = `${t('kiosk.session.worker')}: ${response.employee.name}`
      }
    } else if (selectedLot.value) {
      await startSessionForWorker(response.employee.id, selectedLot.value)
    } else {
      actionMessage.value = `${t('kiosk.session.worker')}: ${response.employee.name}`
    }
    showWorkerPicker.value = false
  } catch (cause) {
    loginError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function restoreActiveSessionContext(session: WorkerSession) {
  try {
    currentShift.value = session.shift === 'night' ? 'night' : 'day'
    if (!orders.value.some((o) => o.id === session.order_id)) {
      orders.value = await listActiveOrders()
      await fetchBoard()
    }
    selectedOrderId.value = session.order_id
    const match = lots.value.find(
      (l) => l.lot_number === session.lot_number && l.production_order_id === session.order_id,
    )
    if (match) {
      selectLot(match)
    }
  } catch {
    // keep current context if restoration fails
  }
}

function nowStamp(): string {
  return nowApiDateTime()
}

function nextClientEventId(action: string): string {
  return `kiosk-${selectedWorkerId.value ?? 0}-${selectedLot.value?.production_order_id ?? 0}-${action}-${Date.now()}`
}

/** "750 000 UZS" for the booked piecework amount, or '' when nothing was booked. */
function formatGross(amount: unknown, currency: unknown): string {
  const value = Number(amount) || 0
  if (value <= 0) return ''
  const code = typeof currency === 'string' ? currency.trim() : ''
  return `${value.toLocaleString()} ${code}`.trim()
}

/** Step 1 (bind): starts a worker session on the given lot + current stage.
 *  Idempotent via client_event_id; offline actions are queued. Shared by the
 *  Start button and the card-swipe auto-bind. */
async function startSessionForWorker(workerId: number, lot: KioskLot): Promise<boolean> {
  const clientEventId = nextClientEventId('start')
  const payload = {
    employee_id: workerId,
    order_id: lot.production_order_id,
    client_event_id: clientEventId,
    shift: currentShift.value,
    lot_number: lot.lot_number,
    stage_code: lot.current_stage_code,
    started_at: nowStamp(),
  }
  submitting.value = true
  try {
    const session = await startSession(payload)
    activeSession.value = session
    currentShift.value = session.shift === 'night' ? 'night' : 'day'
    sessionSecondsLeft.value = SESSION_TIMEOUT_SECONDS
    actionMessage.value = t('kiosk.session.started')
    await loadPieceRates()
    return true
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('start', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
      return true
    }
    actionError.value = formatApiError(cause)
    return false
  } finally {
    submitting.value = false
  }
}

async function submitStartSession() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) {
    actionError.value = t('kiosk.session.selectLotFirst')
    return
  }
  await startSessionForWorker(selectedWorkerId.value, selectedLot.value)
}

async function submitPause() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('pause')
  const payload = { sessionId: activeSession.value.id, paused_at: nowStamp() }
  submitting.value = true
  try {
    activeSession.value = await pauseSession(activeSession.value.id, {
      paused_at: payload.paused_at,
      client_event_id: clientEventId,
    })
    actionMessage.value = t('kiosk.session.pausedMsg')
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('pause', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

async function submitResume() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('resume')
  const payload = { sessionId: activeSession.value.id, resumed_at: nowStamp() }
  submitting.value = true
  try {
    activeSession.value = await resumeSession(activeSession.value.id, {
      resumed_at: payload.resumed_at,
      client_event_id: clientEventId,
    })
    actionMessage.value = t('kiosk.session.resumedMsg')
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('resume', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

async function submitFinish() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('finish')
  const payload = {
    sessionId: activeSession.value.id,
    finished_at: nowStamp(),
    completed_delta: Number(output.completed) || 0,
    rejected_delta: Number(output.rejected) || 0,
    rework_delta: Number(output.rework) || 0,
  }
  submitting.value = true
  try {
    const finished = await finishSession(activeSession.value.id, {
      finished_at: payload.finished_at,
      completed_delta: payload.completed_delta,
      rejected_delta: payload.rejected_delta,
      rework_delta: payload.rework_delta,
      client_event_id: clientEventId,
    })
    activeSession.value = finished
    output.completed = 0
    output.rejected = 0
    output.rework = 0
    actionMessage.value = t('kiosk.session.finishedMsg')
    await refreshLots()
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('finish', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

function sessionStatusLabel(session: WorkerSession): string {
  if (session.status === 'active') return t('kiosk.session.sessionActive')
  if (session.status === 'paused') return t('kiosk.session.sessionPaused')
  return t('kiosk.session.sessionFinished')
}

function elapsedMinutes(session: WorkerSession): number {
  if (!session.total_seconds) return 0
  return Math.round(session.total_seconds / 60)
}

function selectLot(lot: KioskLot) {
  selectedLot.value = lot
  showDefectModal.value = false
  confirmEventId.value = null
  scan.value = ''
  scanError.value = ''
  // Default Good to the lot's remaining on-hand quantity so the operator can
  // confirm the whole remaining lot at this stage without re-entering it.
  output.completed = Math.max(0, Number(lot.current_quantity) || 0)
  output.rejected = 0
  output.rework = 0
  actionError.value = ''
  actionMessage.value = ''
  pieceRates.value = []
  estimatedEarnings.value = null
  // Universal terminal: the lot card only opens after the worker is verified
  // (card swipe / PIN) and bound as the active terminal worker.
  if (!selectedWorkerId.value) {
    showWorkerPicker.value = true
    loginCard.value = ''
    loginWorkerCode.value = ''
    loginError.value = ''
    void nextTick(() => {
      cardInput.value?.focus()
    })
    return
  }
  void loadPieceRates()
  void nextTick(() => {
    scanInput.value?.focus()
  })
}

function openScannedLot() {
  const value = scan.value.trim()
  scanError.value = ''
  if (!value) return

  // 1) Try to match a lot in the currently selected order first.
  const lotMatch = lots.value.find(
    (item) => [item.lot_number, item.basket_number, item.basket_code]
      .some((candidate) => candidate?.toLowerCase() === value.toLowerCase()),
  )
  if (lotMatch) {
    selectedWorkerId.value = null
    activeSession.value = null
    storeWorker(null)
    selectLot(lotMatch)
    scan.value = ''
    return
  }

  // 2) An order-code scan simply confirms the order — all its lots are
  //    already on the universal board.
  const orderMatch = orders.value.find(
    (o) => o.mo_code.toLowerCase() === value.toLowerCase(),
  )
  if (orderMatch) {
    scan.value = ''
    actionMessage.value = `${t('kiosk.session.order')}: ${orderMatch.mo_code}`
    return
  }

  scanError.value = t('kiosk.session.notFound')
}

function openKeypad(): void {
  scanError.value = ''
  showKeypad.value = true
}

function closeKeypad(): void {
  showKeypad.value = false
  void nextTick(() => scanInput.value?.focus())
}

function confirmKeypad(value: string): void {
  scan.value = value
  openScannedLot()
}

function openCodeKeypad(): void {
  loginError.value = ''
  showCodeKeypad.value = true
}

function closeCodeKeypad(): void {
  showCodeKeypad.value = false
}

function clearDefectEntries(): void {
  for (const key of Object.keys(defectEntry)) delete defectEntry[Number(key)]
}

function bumpDefect(lotId: number, field: 'rejected' | 'rework', delta: number): void {
  const current = defectEntryFor(lotId)
  defectEntry[lotId] = { ...current, [field]: Math.max(0, current[field] + delta) }
}

function openDefectModal(): void {
  for (const lot of activeBasketLots.value) {
    if (!defectEntry[lot.id]) defectEntry[lot.id] = { rejected: 0, rework: 0 }
  }
  actionError.value = ''
  showDefectModal.value = true
}

function closeDefectModal(): void {
  showDefectModal.value = false
}

function clearSelection() {
  selectedLot.value = null
  selectedWorkerId.value = null
  activeSession.value = null
  showDefectModal.value = false
  confirmEventId.value = null
  clearDefectEntries()
  storeWorker(null)
  actionMessage.value = ''
  actionError.value = ''
  void nextTick(() => scanInput.value?.focus())
}

/** Basket stage completion. When the basket id is known and every lot sits on
 *  the same stage, one call to `production-execution/baskets/{id}/confirm-stage`
 *  records the output, opens/finishes the worker sessions, books the piecework
 *  accrual and advances every line. Legacy lots without a basket fall back to
 *  the per-lot progress + close calls. */
async function confirmStageCompletion() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value || !selectedLot.value) return

  const basketLots = activeBasketLots.value
  if (basketLots.length === 0) return
  const basketLabel =
    selectedLot.value.basket_number || selectedLot.value.basket_code || selectedLot.value.lot_number
  if (
    !window.confirm(
      t('kiosk.session.closeBasketConfirm', { basket: basketLabel, count: basketLots.length }),
    )
  ) {
    return
  }

  const workerId = selectedWorkerId.value
  submitting.value = true
  const failures: string[] = []
  let closed = 0
  let grossLabel = ''
  try {
    const basketId = selectedLot.value?.production_basket_id ?? null
    const basketStage = selectedLot.value?.current_stage_code ?? ''
    // Lines of the basket that are waiting at the selected lot's stage. The
    // confirm endpoint closes exactly these; already advanced lines keep going.
    const stageLots = basketLots.filter((lot) => lot.current_stage_code === basketStage)

    if (basketId !== null && basketStage !== '' && stageLots.length > 0) {
      // Atomic basket confirmation: output, sessions, accruals and advancing.
      const stageCode = basketStage
      if (!stageRate.value) await loadPieceRates()
      const rate =
        stageRate.value ?? pieceRates.value.find((row) => row.stage_code === stageCode) ?? null
      if (!rate) {
        actionError.value = t('kiosk.session.noPieceRate')
        return
      }

      const eventId = confirmEventId.value ?? nextClientEventId('confirm-stage')
      confirmEventId.value = eventId
      try {
        const result = await confirmBasketStage(basketId, {
          client_event_id: eventId,
          stage_code: stageCode,
          employee_id: workerId,
          shift: currentShift.value,
          rate_per_pair: Number(rate.rate_per_pair) || 0,
          currency_code: rate.currency_code || undefined,
          piece_rate_id: (rate.source ?? 'piece_rate') === 'piece_rate' ? rate.id : undefined,
          output_date: new Date().toLocaleDateString('en-CA'),
          started_at: activeSession.value?.started_at ?? nowStamp(),
          finished_at: nowStamp(),
          lines: stageLots.map((lot) => {
            const defects = defectEntryFor(lot.id)
            const line: {
              production_lot_id: number
              completed_quantity?: number
              rejected_quantity?: number
              rework_quantity?: number
            } = { production_lot_id: lot.id }
            // Without defects the backend defaults `completed_quantity` to the
            // line's remaining quantity; with defects the good quantity must be
            // explicit so `completed + rejected` stays inside the line plan.
            if (defects.rejected > 0 || defects.rework > 0) {
              line.completed_quantity = Math.max(
                0,
                (Number(lot.current_quantity) || 0) - defects.rejected - defects.rework,
              )
              line.rejected_quantity = defects.rejected
              line.rework_quantity = defects.rework
            }
            return line
          }),
        })
        closed = Number(result?.closed_line_count ?? 0) || stageLots.length
        grossLabel = formatGross(result?.gross_amount, result?.currency_code)
        confirmEventId.value = null
        // The endpoint finishes the open worker session for this lot/stage.
        activeSession.value = null
      } catch (cause) {
        actionError.value = formatApiError(cause)
        return
      }
    } else {
      // Legacy path: record each lot's quantities, then close per lot.
      console.warn('[kiosk] basket confirm skipped', {
        production_basket_id: basketId,
        stage_code: basketStage,
        stage_lots: stageLots.length,
      })
      for (const lot of basketLots) {
        const defects = defectEntryFor(lot.id)
        const completed = Math.max(0, Number(lot.current_quantity) || 0)
        const session = activeSession.value
        const sessionForLot =
          session !== null && session.lot_number === lot.lot_number && session.status !== 'finished'

        if (completed > 0) {
          try {
            if (sessionForLot) {
              activeSession.value = await finishSession(session.id, {
                finished_at: nowStamp(),
                completed_delta: completed,
                rejected_delta: defects.rejected,
                rework_delta: defects.rework,
                client_event_id: nextClientEventId('finish'),
              })
            } else {
              await recordProgress(
                lot.production_order_id,
                lot.lot_number,
                lot.current_stage_code,
                {
                  completed_delta: completed,
                  rejected_delta: defects.rejected,
                  rework_delta: defects.rework,
                  employee_id: workerId,
                },
              )
            }
          } catch {
            // A lot stage may already hold its quantity (repeated confirmation);
            // closing still decides whether the stage actually failed.
          }
        }
      }

      const session = activeSession.value
      if (session && session.status !== 'finished') {
        try {
          activeSession.value = await finishSession(session.id, {
            finished_at: nowStamp(),
            completed_delta: 0,
            rejected_delta: 0,
            rework_delta: 0,
            client_event_id: nextClientEventId('finish'),
          })
        } catch {
          // The stage results below are still the authoritative outcome.
        }
      }

      for (const lot of basketLots) {
        try {
          await closeStage(lot.production_order_id, lot.lot_number, lot.current_stage_code, {
            employee_id: workerId,
          })
          closed += 1
        } catch (cause) {
          failures.push(`${lot.lot_number}: ${formatApiError(cause)}`)
        }
      }
    }

    await refreshLots()
    if (failures.length) {
      actionError.value = failures.join(' · ')
      return
    }
    selectedLot.value = null
    showDefectModal.value = false
    output.rejected = 0
    output.rework = 0
    clearDefectEntries()
    actionMessage.value = grossLabel
      ? `${t('kiosk.session.basketStageClosed', { count: closed })} · ${grossLabel}`
      : t('kiosk.session.basketStageClosed', { count: closed })
    void nextTick(() => scanInput.value?.focus())
  } finally {
    submitting.value = false
  }
}

/** Captures the defect / rework quantities entered per basket lot. They are
 *  submitted together with the lot quantities when the stage is completed. */
function recordDefect(): void {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  const lots = activeBasketLots.value.filter((lot) => {
    const entry = defectEntryFor(lot.id)
    return entry.rejected > 0 || entry.rework > 0
  })
  if (lots.length === 0) {
    actionError.value = t('kiosk.session.enterDefectQty')
    return
  }
  showDefectModal.value = false
  actionMessage.value = t('kiosk.session.defectsRecorded', {
    lot: lots.map((lot) => lot.lot_number).join(', '),
  })
}

/** Mirrors recorded deltas onto the active session so the Step 2 completion
 *  prompt can compare the bound worker's output against the lot's on-hand
 *  quantity for the current stage. */
function addSessionOutput(completed: number, rejected: number, rework: number): void {
  const session = activeSession.value
  if (!session) return
  activeSession.value = {
    ...session,
    completed_quantity: Number(session.completed_quantity || 0) + completed,
    rejected_quantity: Number(session.rejected_quantity || 0) + rejected,
    rework_quantity: Number(session.rework_quantity || 0) + rework,
  }
}

async function submitOutput() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) return
  if (output.completed <= 0 && output.rejected <= 0 && output.rework <= 0) return

  const completedDelta = Number(output.completed) || 0
  const rejectedDelta = Number(output.rejected) || 0
  const reworkDelta = Number(output.rework) || 0

  submitting.value = true
  try {
    await recordProgress(
      selectedLot.value.production_order_id,
      selectedLot.value.lot_number,
      selectedLot.value.current_stage_code,
      {
        completed_delta: completedDelta,
        rejected_delta: rejectedDelta,
        rework_delta: reworkDelta,
        employee_id: selectedWorkerId.value,
      },
    )
    actionMessage.value = t('kiosk.session.progressRecorded', { lot: selectedLot.value.lot_number })
    addSessionOutput(completedDelta, rejectedDelta, reworkDelta)
    output.completed = 0
    output.rejected = 0
    output.rework = 0
    await refreshLots()
    recomputeEstimate()

    // Step 2 — when the bound worker's output reaches the lot's on-hand
    // quantity for the current stage, ask to close it (gated + confirmed).
    const lotQty = Number(selectedLot.value?.current_quantity) || 0
    const session = activeSession.value
    const sessionOutput =
      (Number(session?.completed_quantity) || 0) + (Number(session?.rejected_quantity) || 0)
    if (
      selectedLot.value &&
      session &&
      session.lot_number === selectedLot.value.lot_number &&
      session.status !== 'finished' &&
      lotQty >= 1 &&
      sessionOutput >= lotQty
    ) {
      if (window.confirm(t('kiosk.session.closeConfirm'))) {
        await doCloseStage()
      } else {
        actionMessage.value = t('kiosk.session.stageComplete')
      }
    }
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

/** Step 2 (close): closes the current stage of the selected lot, recording
 *  the bound worker for the audit trail. Shared by the explicit Close button
 *  and the completion prompt so the confirmation is never asked twice. */
async function doCloseStage(): Promise<boolean> {
  if (!selectedWorkerId.value || !selectedLot.value) return false
  submitting.value = true
  try {
    await closeStage(
      selectedLot.value.production_order_id,
      selectedLot.value.lot_number,
      selectedLot.value.current_stage_code,
      { employee_id: selectedWorkerId.value },
    )
    actionMessage.value = t('kiosk.session.stageClosed', { lot: selectedLot.value.lot_number })
    selectedLot.value = null
    await refreshLots()
    return true
  } catch (cause) {
    actionError.value = formatApiError(cause)
    return false
  } finally {
    submitting.value = false
  }
}

async function submitClose() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) return
  if (!window.confirm(t('kiosk.session.closeConfirm'))) return
  await doCloseStage()
}

async function refreshLots() {
  await fetchBoard()
}

/** Resolve the stage rate: `piece-rates` first, `piece-rates-bom` only when it returns nothing. */
async function loadPieceRates() {
  const lot = selectedLot.value
  const stageCode = lot?.current_stage_code
  if (!stageCode || !selectedWorkerId.value || !lot) return
  const order = orders.value.find((item) => item.id === lot.production_order_id)
  const shift = currentShift.value
  try {
    const configured = await fetchPieceRates({
      stage_code: stageCode,
      product_id: order?.product_id,
      employee_id: selectedWorkerId.value,
      shift,
    })
    if (configured.length > 0) {
      // The primary endpoint answered (configured rate or its own BOM fallback).
      pieceRates.value = configured
    } else {
      // Nothing at all — the second API reads the rate carried by the BOM of
      // the selected lot's product (order-scoped).
      pieceRates.value = await fetchBomPieceRates({
        stage_code: stageCode,
        production_order_id: lot.production_order_id,
        product_id: order?.product_id,
        shift,
      })
    }
  } catch {
    pieceRates.value = []
  }
  recomputeEstimate()
}

function recomputeEstimate() {
  const stageCode = selectedLot.value?.current_stage_code
  if (!stageCode || !activeSession.value) {
    estimatedEarnings.value = null
    return
  }
  const completed = Number(activeSession.value.completed_quantity) || 0
  const rate = pieceRates.value.find(
    (r) => r.stage_code === stageCode && r.shift === currentShift.value,
  )
  if (!rate) {
    estimatedEarnings.value = null
    return
  }
  estimatedEarnings.value = completed * Number(rate.rate_per_pair) || 0
  estimatedCurrency.value = rate.currency_code || ''
}

const stageRate = computed(() => {
  const stageCode = selectedLot.value?.current_stage_code
  if (!stageCode) return null
  return (
    pieceRates.value.find(
      (r) => r.stage_code === stageCode && r.shift === currentShift.value,
    ) ?? null
  )
})

/** "2 500 UZS" for the current stage rate, empty when none resolves. */
const stageRateLabel = computed(() => {
  const rate = stageRate.value
  if (!rate) return ''
  return `${Number(rate.rate_per_pair).toLocaleString()} ${rate.currency_code || ''}`.trim()
})

const stageRateFromBom = computed(
  () => !!stageRate.value && (stageRate.value.source ?? 'piece_rate') !== 'piece_rate',
)

const shiftLabel = computed(() =>
  currentShift.value === 'night' ? t('kiosk.session.shiftNight') : t('kiosk.session.shiftDay'),
)

function setShift(shift: 'day' | 'night') {
  if (activeSession.value || currentShift.value === shift) return
  currentShift.value = shift
  void loadPieceRates()
}

function openDowntimeModal() {
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  downtimeForm.reason_code = downtimeReasons.value[0]?.code ?? ''
  downtimeForm.notes = ''
  downtimeForm.machine_id = null
  actionError.value = ''
  showDowntime.value = true
}

async function submitDowntime() {
  actionError.value = ''
  if (!downtimeForm.reason_code) {
    actionError.value = t('kiosk.session.selectReason')
    return
  }
  submitting.value = true
  try {
    await openDowntime({
      reason_code: downtimeForm.reason_code,
      machine_id: downtimeForm.machine_id ?? undefined,
      order_id: selectedLot.value?.production_order_id ?? null,
      lot_number: selectedLot.value?.lot_number ?? null,
      start_time: nowApiDateTime(),
      notes: downtimeForm.notes.trim() || undefined,
    })
    showDowntime.value = false
    actionMessage.value = t('kiosk.session.reportDowntime')
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <!-- Worker picker -->
  <div v-if="showWorkerPicker" class="worker-picker">
    <div class="worker-picker__panel">
      <form class="card-login" @submit.prevent="submitWorkerLogin">
        <h2>{{ t('kiosk.session.activateWorker') }}</h2>
        <p class="card-login__title">{{ t('kiosk.session.swipeOrEnterCode') }}</p>
        <label>{{ t('kiosk.session.cardUid') }}
          <input
            ref="cardInput"
            v-model="loginCard"
            type="text"
            autocomplete="off"
            :placeholder="t('kiosk.session.cardPlaceholder')"
          />
        </label>
        <div class="activation-divider"><span>{{ t('kiosk.session.or') }}</span></div>
        <label>{{ t('kiosk.session.workerCode') }}
          <span class="card-login__field">
            <input v-model="loginWorkerCode" type="text" autocomplete="off" :placeholder="t('kiosk.session.workerCodePlaceholder')" />
            <button
              type="button"
              class="card-login__keypad"
              :aria-label="t('kiosk.session.openNumpad')"
              @click="openCodeKeypad"
            >
              <Keyboard :size="22" aria-hidden="true" />
            </button>
          </span>
        </label>
        <small v-if="loginError" class="error">{{ loginError }}</small>
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? t('kiosk.session.recording') : t('kiosk.session.activate') }}
        </button>
      </form>

      <button type="button" class="btn-secondary" @click="showWorkerPicker = false">
        {{ t('kiosk.session.clear') }}
      </button>
    </div>
  </div>

  <NumericKeypadModal
    v-if="showCodeKeypad"
    v-model="loginWorkerCode"
    :title="t('kiosk.session.workerCode')"
    :done-label="t('kiosk.session.numpadDone')"
    :clear-label="t('kiosk.session.clear')"
    :backspace-label="t('kiosk.session.numpadBackspace')"
    :close-label="t('kiosk.session.numpadClose')"
    @close="closeCodeKeypad"
  />

  <!-- Defect / rework input for every lot of the opened basket -->
  <div v-if="showDefectModal" class="defect-modal" @click.self="closeDefectModal">
    <section
      class="defect-panel"
      role="dialog"
      aria-modal="true"
      :aria-label="t('kiosk.session.enterDefects')"
    >
      <header class="defect-panel__head">
        <p class="defect-panel__title">{{ t('kiosk.session.enterDefects') }}</p>
        <button
          type="button"
          class="defect-panel__close"
          :aria-label="t('kiosk.session.closeDialog')"
          @click="closeDefectModal"
        >
          <X :size="20" aria-hidden="true" />
        </button>
      </header>
      <div class="defect-panel__list">
        <div v-for="lot in activeBasketLots" :key="lot.id" class="defect-row">
          <div class="defect-row__lot">
            <strong>{{ lot.lot_number }}</strong>
            <small>{{ lot.color_variant || stageName(lot.current_stage_code) }}</small>
          </div>
          <div class="stepper stepper--reject">
            <span class="stepper__label">
              <CircleX :size="16" aria-hidden="true" /> {{ t('kiosk.session.rejected') }}
            </span>
            <div class="stepper__controls">
              <button type="button" class="stepper__jump" @click="bumpDefect(lot.id, 'rejected', -10)">-10</button>
              <button type="button" @click="bumpDefect(lot.id, 'rejected', -1)">−</button>
              <strong>{{ defectEntryFor(lot.id).rejected }}</strong>
              <button type="button" @click="bumpDefect(lot.id, 'rejected', 1)">+</button>
              <button type="button" class="stepper__jump" @click="bumpDefect(lot.id, 'rejected', 10)">+10</button>
            </div>
          </div>
          <div class="stepper stepper--rework">
            <span class="stepper__label">
              <RotateCcw :size="16" aria-hidden="true" /> {{ t('kiosk.session.rework') }}
            </span>
            <div class="stepper__controls">
              <button type="button" class="stepper__jump" @click="bumpDefect(lot.id, 'rework', -10)">-10</button>
              <button type="button" @click="bumpDefect(lot.id, 'rework', -1)">−</button>
              <strong>{{ defectEntryFor(lot.id).rework }}</strong>
              <button type="button" @click="bumpDefect(lot.id, 'rework', 1)">+</button>
              <button type="button" class="stepper__jump" @click="bumpDefect(lot.id, 'rework', 10)">+10</button>
            </div>
          </div>
        </div>
      </div>
      <p v-if="actionError" class="error defect-panel__error">{{ actionError }}</p>
      <footer class="defect-panel__footer">
        <button type="button" class="btn-secondary btn-big" @click="closeDefectModal">
          {{ t('kiosk.session.closeDialog') }}
        </button>
        <button type="button" class="btn-warn btn-big" :disabled="submitting" @click="recordDefect">
          {{ t('kiosk.session.recordDefects') }}
        </button>
      </footer>
    </section>
  </div>

  <header class="terminal-header">
    <div class="terminal-clock">
      <span>{{ dateLabel }}</span>
      <strong>{{ clockLabel }}</strong>
    </div>
    <div class="terminal-actions">
      <div class="language-switch" role="group" :aria-label="t('kiosk.session.language')">
        <button v-for="code in ['uz', 'ru', 'en'] as const" :key="code" type="button"
          :class="{ active: locale === code }" :aria-pressed="locale === code" @click="setLanguage(code)">
          {{ code.toUpperCase() }}
        </button>
      </div>
      <span class="timeout-chip" :class="{ active: selectedWorkerId && selectedLot, low: selectedWorkerId && selectedLot && sessionSecondsLeft <= 15 }"
        :title="t('kiosk.session.sessionTimeout', { time: sessionCountdown })">
        <Timer :size="18" aria-hidden="true" />
        <span>
          <small>{{ t('kiosk.session.timeout') }}</small>
          <strong>{{ selectedWorkerId && selectedLot ? sessionCountdown : '—' }}</strong>
        </span>
      </span>
    </div>
  </header>

  <!-- Offline / pending sync banner -->
  <div
    v-if="!isOnline || pendingCount > 0"
    class="sync-banner"
    :class="{ 'sync-banner--offline': !isOnline }"
    role="status"
  >
    <span class="sync-banner__icon" aria-hidden="true">⟳</span>
    <span class="sync-banner__text">
      <strong>{{ !isOnline ? t('kiosk.session.offline') : t('kiosk.session.pending') }}</strong>
      <small v-if="pendingCount > 0">{{ t('kiosk.session.pendingCount', { count: pendingCount }) }} — {{ t('kiosk.session.queuedMessage') }}</small>
      <small v-else>{{ t('kiosk.session.queuedMessage') }}</small>
    </span>
    <button
      type="button"
      class="btn-secondary sync-banner__action"
      :disabled="syncing || !isOnline || pendingCount === 0"
      @click="flushQueue"
    >
      {{ syncing ? t('kiosk.session.syncing') : t('kiosk.session.syncNow') }}
    </button>
  </div>

  <div v-if="showLegacyBoard" class="page-heading">
    <div>
      <p class="eyebrow">{{ t('kiosk.session.eyebrow') }}</p>
      <h2>{{ t('kiosk.session.title') }}</h2>
      <p>{{ t('kiosk.session.description') }}</p>
    </div>
    <button v-if="selectedLot && selectedWorker" type="button" class="btn-worker" @click="pickWorker">
      <span class="btn-worker__avatar">{{ selectedWorker.first_name.charAt(0) }}</span>
      <span>
        <small>{{ t('kiosk.session.worker') }}</small>
        <strong>{{ workerLabel(selectedWorker) }}</strong>
      </span>
    </button>
    <button v-else-if="selectedLot" type="button" class="btn-primary" @click="pickWorker">
      {{ t('kiosk.session.selectWorker') }}
    </button>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('kiosk.session.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('kiosk.session.retry') }}</button>
  </div>

  <!-- No orders -->
  <div v-else-if="orders.length === 0" class="state-message">
    <p>{{ t('kiosk.session.noOrders') }}</p>
  </div>

  <template v-else>
    <section v-if="!selectedLot" class="basket-screen" aria-labelledby="basket-entry-title">
      <h3 id="basket-entry-title">{{ t('kiosk.session.enterBasket') }}</h3>
      <form class="basket-entry" @submit.prevent="openScannedLot">
        <div class="basket-entry__field">
          <input ref="scanInput" v-model="scan" type="text" autocomplete="off" autofocus
            :placeholder="t('kiosk.session.basketNumber')" class="basket-entry__input" />
          <button
            type="button"
            class="basket-entry__keypad"
            :aria-label="t('kiosk.session.openNumpad')"
            @click="openKeypad"
          >
            <Keyboard :size="24" aria-hidden="true" />
          </button>
        </div>
        <button type="submit" class="basket-entry__submit">{{ t('kiosk.session.scan') }}</button>
      </form>
      <small v-if="scanError" class="error basket-entry__error">{{ scanError }}</small>
      <p v-if="actionMessage" class="action-message basket-entry__message">{{ actionMessage }}</p>
    </section>

    <NumericKeypadModal
      v-if="showKeypad"
      v-model="scan"
      :title="t('kiosk.session.basketNumber')"
      :hint="t('kiosk.session.numpadHint')"
      :done-label="t('kiosk.session.numpadDone')"
      :clear-label="t('kiosk.session.clear')"
      :backspace-label="t('kiosk.session.numpadBackspace')"
      :close-label="t('kiosk.session.numpadClose')"
      @confirm="confirmKeypad"
      @close="closeKeypad"
    />

    <section v-if="selectedLot && selectedWorker" class="kiosk-workspace">
      <aside class="worker-profile">
        <div class="worker-profile__avatar">
          <img v-if="selectedWorker.photo_url" :src="selectedWorker.photo_url" :alt="workerLabel(selectedWorker)" />
          <span v-else aria-hidden="true">{{ selectedWorker.first_name.charAt(0) }}</span>
        </div>
        <small>{{ t('kiosk.session.worker') }}</small>
        <strong>{{ workerLabel(selectedWorker) }}</strong>
        <span>{{ selectedWorker.employee_code }}</span>
        <button type="button" class="btn-secondary" @click="pickWorker">{{ t('kiosk.session.changeWorker') }}</button>
      </aside>

      <div class="kiosk-task">
        <div class="kiosk-task__body">
          <header class="kiosk-task__header">
            <div>
              <span class="kiosk-task__basket-label">{{ t('kiosk.session.basketNumber') }}</span>
              <h3 class="kiosk-task__basket">{{ selectedLot.basket_number || selectedLot.basket_code || selectedLot.lot_number }}</h3>
            </div>
            <div class="kiosk-task__meta">
            <span class="kiosk-task__stage-label">{{ stageName(selectedLot.current_stage_code) }}</span>
            <span v-if="stageRateLabel" class="kiosk-task__rate">
              {{ stageRateLabel }}
              <small v-if="stageRateFromBom">{{ t('kiosk.session.bomRate') }}</small>
            </span>
          </div>
          </header>

          <div class="product-lines">
            <h4>{{ t('kiosk.session.productLines') }}</h4>
            <div class="product-lines__table" role="table">
              <div class="product-lines__row product-lines__head" role="row">
                <span>{{ t('kiosk.session.product') }}</span><span>{{ t('kiosk.session.color') }}</span>
                <span>{{ t('kiosk.session.size') }}</span><span>{{ t('kiosk.session.quantity') }}</span>
                <span class="defect-qty defect-qty--reject">{{ t('kiosk.session.rejected') }}</span>
                <span class="defect-qty defect-qty--rework">{{ t('kiosk.session.rework') }}</span>
              </div>
              <div v-for="line in productLines" :key="line.key" class="product-lines__row">
                <strong>{{ line.product || '—' }}</strong><span>{{ line.color || '—' }}</span>
                <span>{{ line.size || '—' }}</span><b>{{ line.quantity.toLocaleString() }}</b>
                <span
                  v-if="line.defectsLead"
                  class="defect-qty defect-qty--reject"
                  :class="{ 'defect-qty--empty': !line.rejected }"
                >{{ line.rejected }}</span>
                <span v-else class="defect-qty"></span>
                <span
                  v-if="line.defectsLead"
                  class="defect-qty defect-qty--rework"
                  :class="{ 'defect-qty--empty': !line.rework }"
                >{{ line.rework }}</span>
                <span v-else class="defect-qty"></span>
              </div>
            </div>
            <div class="basket-total">
              <span>{{ t('kiosk.session.totalBasketQuantity') }}</span>
              <strong>{{ basketTotalQuantity.toLocaleString() }}</strong>
            </div>
          </div>

          <small v-if="actionError" class="error kiosk-task__error">{{ actionError }}</small>
          <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
        </div>

        <footer class="kiosk-footer">
          <button type="button" class="btn-success btn-big" :disabled="submitting" @click="confirmStageCompletion">
            <CircleCheck :size="20" aria-hidden="true" /> {{ t('kiosk.session.confirmCompletion') }}
          </button>
          <button type="button" class="btn-warn btn-big" :aria-pressed="showDefectModal" @click="openDefectModal">
            <CircleX :size="20" aria-hidden="true" /> {{ t('kiosk.session.enterDefects') }}
          </button>
          <button type="button" class="btn-secondary btn-big" :disabled="submitting" @click="clearSelection">
            <ArrowLeft :size="20" aria-hidden="true" /> {{ t('kiosk.session.backToBasket') }}
          </button>
        </footer>
      </div>
    </section>
    <!-- Production orders (planned + in progress) — visible once a worker is
         bound. Tapping a card filters the lot board to that order. -->
    <section v-if="showLegacyBoard" class="panel orders-panel">
      <div class="panel-head">
        <h3>{{ t('kiosk.session.orders') }} — {{ orderCards.length }}</h3>
        <button
          v-if="orderFilterId !== null"
          type="button"
          class="btn-secondary orders-panel__reset"
          @click="orderFilterId = null"
        >
          {{ t('kiosk.session.showAllOrders') }}
        </button>
      </div>
      <div v-if="orderCards.length === 0" class="empty-inline">{{ t('kiosk.session.noOrders') }}</div>
      <div v-else class="order-grid">
        <button
          v-for="entry in orderCards"
          :key="entry.order.id"
          type="button"
          :class="['order-card', { selected: orderFilterId === entry.order.id }]"
          :aria-pressed="orderFilterId === entry.order.id"
          @click="toggleOrderFilter(entry.order.id)"
        >
          <span class="order-card__top">
            <span class="order-card__code">{{ entry.order.mo_code }}</span>
            <span :class="['order-card__status', `order-card__status--${entry.order.status}`]">
              {{ orderStatusLabel(entry.order.status) }}
            </span>
          </span>
          <span v-if="entry.order.product_name" class="order-card__product">{{ entry.order.product_name }}</span>
          <span class="order-card__meta">
            {{ t('kiosk.session.plannedQty') }}: {{ entry.order.planned_quantity }}
            <template v-if="entry.order.department_name"> · {{ entry.order.department_name }}</template>
            · {{ entry.lotCount }} {{ t('kiosk.session.activeLots') }}
          </span>
        </button>
      </div>
    </section>

    <div v-if="showLegacyBoard" class="session-grid">
      <!-- Lot list -->
      <section class="panel">
        <h3>
          {{ t('kiosk.session.lot') }} — {{ visibleLots.length }} {{ t('kiosk.session.activeLots') }}
          <small v-if="orderFilterId !== null" class="filter-note">{{ t('kiosk.session.filteredByOrder') }}</small>
        </h3>

        <!-- Scan -->
        <form class="scanner" @submit.prevent="openScannedLot">
          <input
            ref="scanInput"
            v-model="scan"
            type="text"
            autocomplete="off"
            :placeholder="t('kiosk.session.scanOrderOrLot')"
            class="scan-input"
          />
          <button type="submit" class="btn-primary">{{ t('kiosk.session.scan') }}</button>
        </form>
        <small v-if="scanError" class="error">{{ scanError }}</small>

        <div v-if="visibleLots.length === 0" class="empty-inline">{{ t('kiosk.session.noLots') }}</div>
        <div v-else class="lot-grid">
          <button
            v-for="lot in visibleLots"
            :key="lot.id"
            type="button"
            :class="['lot-card', { selected: selectedLot?.id === lot.id }]"
            @click="selectLot(lot)"
          >
            <span class="lot-card__top">
              <span class="lot-card__order">{{ lot.order_code }}</span>
              <span class="lot-card__qty">{{ lot.current_quantity }} / {{ lot.initial_quantity }}</span>
            </span>
            <span class="lot-card__code">{{ lot.lot_number }}</span>
            <span v-if="lot.product_name" class="lot-card__product">{{ lot.product_name }}</span>
            <span class="lot-card__stage">
              {{ stageName(lot.current_stage_code) }}
              <span v-if="lot.color_variant" class="lot-card__color">{{ lot.color_variant }}</span>
            </span>
          </button>
        </div>
      </section>

    </div>

    <!-- Chosen lot — the entry card opens as a modal with a back button to the
         work-session (lot list) page. -->
    <div v-if="showLegacyBoard && selectedLot && selectedWorkerId" class="work-overlay" @click.self="clearSelection">
      <section class="work-modal" role="dialog" aria-modal="true" :aria-label="t('kiosk.session.selectedLot')">
        <header class="work-modal__header">
          <button type="button" class="btn-back" @click="clearSelection">
            <ArrowLeft :size="18" aria-hidden="true" />
            {{ t('kiosk.session.backToWork') }}
          </button>
          <div class="work-modal__headline">
            <h3 class="work-modal__operation">{{ stageName(selectedLot.current_stage_code) }}</h3>
            <div class="work-modal__order">
              <span v-if="selectedLot.order_code" class="work-modal__mo">{{ selectedLot.order_code }}</span>
              <span v-if="selectedLot.product_name" class="work-modal__product">{{ selectedLot.product_name }}</span>
            </div>
            <p class="selected-meta">
              {{ t('kiosk.session.selectedLot') }}: {{ selectedLot.lot_number }} ·
              {{ selectedLot.current_quantity }} / {{ selectedLot.initial_quantity }}
            </p>
          </div>
        </header>

          <!-- Shift selection: rates differ per shift (day / night). Locked
               while a session is active — the session records its own shift. -->
          <div class="shift-toggle" role="group" :aria-label="t('kiosk.session.shift')">
            <span class="shift-toggle__label">{{ t('kiosk.session.shift') }}</span>
            <button
              v-for="s in ['day', 'night'] as const"
              :key="s"
              type="button"
              class="shift-toggle__btn"
              :class="{ active: currentShift === s }"
              :disabled="!!activeSession"
              @click="setShift(s)"
            >
              <span class="shift-toggle__icon" aria-hidden="true">
                <Sun v-if="s === 'day'" :size="18" />
                <Moon v-else :size="18" />
              </span>
              {{ s === 'day' ? t('kiosk.session.shiftDay') : t('kiosk.session.shiftNight') }}
            </button>
          </div>

          <!-- Step 1: no worker bound yet — prompt to swipe / select -->
          <div v-if="!selectedWorker" class="bind-hint">
            {{ t('kiosk.session.swipeHint') }}
          </div>

          <!-- Output steppers -->
          <div class="stepper-row">
            <div class="stepper stepper--good">
              <span class="stepper__label"><CircleCheck :size="18" aria-hidden="true" /> {{ t('kiosk.session.completed') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.completed = Math.max(0, output.completed - 1)">−</button>
                <strong>{{ output.completed }}</strong>
                <button type="button" @click="output.completed += 1">+</button>
              </div>
            </div>
            <div class="stepper stepper--reject">
              <span class="stepper__label"><CircleX :size="18" aria-hidden="true" /> {{ t('kiosk.session.rejected') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.rejected = Math.max(0, output.rejected - 1)">−</button>
                <strong>{{ output.rejected }}</strong>
                <button type="button" @click="output.rejected += 1">+</button>
              </div>
            </div>
            <div class="stepper stepper--rework">
              <span class="stepper__label"><RotateCcw :size="18" aria-hidden="true" /> {{ t('kiosk.session.rework') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.rework = Math.max(0, output.rework - 1)">−</button>
                <strong>{{ output.rework }}</strong>
                <button type="button" @click="output.rework += 1">+</button>
              </div>
            </div>
          </div>

          <!-- Worker session -->
          <div class="session-bar">
            <div class="session-status">
              <span class="session-status__label">{{ t('kiosk.session.sessionStatus') }}</span>
              <strong v-if="activeSession" :class="`session-${activeSession.status}`">
                {{ sessionStatusLabel(activeSession) }}
              </strong>
              <strong v-else class="session-none">—</strong>
              <span v-if="activeSession && activeSession.total_seconds" class="session-elapsed">
                {{ t('kiosk.session.elapsed', { minutes: elapsedMinutes(activeSession) }) }}
              </span>
            </div>
            <div
              class="session-actions"
              :class="{ 'session-actions--single': !activeSession || activeSession.status === 'finished' }"
            >
              <button
                v-if="!activeSession || activeSession.status === 'finished'"
                type="button"
                class="btn-primary btn-big"
                :disabled="submitting"
                @click="submitStartSession"
              >
                <Play :size="18" aria-hidden="true" />
                {{ submitting ? t('kiosk.session.starting') : t('kiosk.session.startSession') }}
              </button>
              <button
                v-if="activeSession && activeSession.status === 'active'"
                type="button"
                class="btn-secondary btn-big"
                :disabled="submitting"
                @click="submitPause"
              >
                <Pause :size="18" aria-hidden="true" />
                {{ t('kiosk.session.pause') }}
              </button>
              <button
                v-if="activeSession && activeSession.status === 'paused'"
                type="button"
                class="btn-secondary btn-big"
                :disabled="submitting"
                @click="submitResume"
              >
                <Play :size="18" aria-hidden="true" />
                {{ t('kiosk.session.resume') }}
              </button>
              <button
                v-if="activeSession && activeSession.status !== 'finished'"
                type="button"
                class="btn-success btn-big"
                :disabled="submitting"
                @click="submitFinish"
              >
                <CircleCheck :size="18" aria-hidden="true" />
                {{ submitting ? t('kiosk.session.finishing') : t('kiosk.session.finish') }}
              </button>
            </div>
          </div>

          <!-- Live earnings estimate -->
          <div v-if="activeSession && activeSession.status !== 'finished'" class="estimate-bar">
            <span class="estimate-bar__label">{{ t('kiosk.session.liveEstimate') }}</span>
            <strong v-if="estimatedEarnings != null" class="estimate-bar__value">
              {{ estimatedEarnings.toLocaleString() }}
              <small>{{ estimatedCurrency }}</small>
            </strong>
            <span v-else-if="stageRate" class="estimate-bar__hint">
              {{ t('kiosk.session.ratePerPair') }} ({{ shiftLabel }}): {{ stageRate.rate_per_pair }}
            </span>
            <span v-else class="estimate-bar__hint">{{ t('kiosk.session.noRate') }}</span>
          </div>

          <small v-if="actionError" class="error">{{ actionError }}</small>
          <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

          <div class="action-row">
            <button
              type="button"
              class="btn-primary btn-big"
              :disabled="submitting || (output.completed <= 0 && output.rejected <= 0 && output.rework <= 0)"
              @click="submitOutput"
            >
              {{ submitting ? t('kiosk.session.recording') : t('kiosk.session.record') }}
            </button>
            <button type="button" class="btn-success btn-big" :disabled="submitting" @click="submitClose">
              {{ t('kiosk.session.closeStage') }}
            </button>
            <button type="button" class="btn-warn btn-big" :disabled="submitting" @click="openDowntimeModal">
              {{ t('kiosk.session.downtime') }}
            </button>
          </div>
      </section>
    </div>

    <!-- Downtime modal -->
    <div v-if="showDowntime" class="modal-overlay" @click.self="showDowntime = false">
      <form class="modal" @submit.prevent="submitDowntime">
        <h3>{{ t('kiosk.session.reportDowntime') }}</h3>
        <label>{{ t('kiosk.session.selectReason') }}
          <select v-model="downtimeForm.reason_code" required>
            <option v-for="r in downtimeReasons" :key="r.code" :value="r.code">{{ reasonLabel(r.code, r.name) }}</option>
          </select>
        </label>
        <label>{{ t('kiosk.session.machine') }}
          <select v-model.number="downtimeForm.machine_id">
            <option :value="null">{{ t('kiosk.session.noMachine') }}</option>
            <option v-for="e in equipment" :key="e.id" :value="e.id">{{ e.name }}</option>
          </select>
          <small v-if="equipment.length === 0" class="error">{{ t('kiosk.session.noMachines') }}</small>
        </label>
        <label>{{ t('kiosk.session.notes') }}
          <input v-model="downtimeForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showDowntime = false">{{ t('kiosk.state.retry') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('kiosk.session.reporting') : t('kiosk.session.report') }}
          </button>
        </div>
      </form>
    </div>
  </template>
</template>

<style scoped>
.terminal-header {
  position: sticky;
  top: 0;
  z-index: 35;
  display: grid;
  grid-template-columns: minmax(190px, 1fr) auto;
  align-items: center;
  gap: 20px;
  width: 100%;
  flex: 0 0 auto;
  margin: 0;
  padding: 14px 24px;
  color: #edf7ff;
  background: #081725;
  border-bottom: 1px solid #27405a;
}
/* The shared shell adds a 14px bottom margin to `.app-content > header`. */
.app-main .app-content > header.terminal-header { margin-bottom: 0; }
.terminal-clock { display: flex; flex-direction: column; }
.terminal-clock span { color: #9eb5ca; font-size: 12px; font-weight: 650; text-transform: capitalize; }
.terminal-clock strong { font-variant-numeric: tabular-nums; font-size: 27px; line-height: 1.15; letter-spacing: 0.04em; }
.terminal-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; }
.language-switch { display: inline-flex; gap: 4px; padding: 4px; border: 1px solid #324b64; border-radius: 11px; background: #0b1927; }
.language-switch button {
  min-width: 48px;
  min-height: 44px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  color: #a9bbcc;
  background: transparent;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.language-switch button.active { color: #06202a; background: #52c6d6; }
.language-switch button:focus-visible { outline: 3px solid rgb(82 198 214 / 35%); outline-offset: 2px; }
.timeout-chip { display: inline-flex; min-height: 52px; align-items: center; gap: 9px; padding: 5px 12px; border: 1px solid #34495f; border-radius: 11px; color: #70869a; background: #0b1927; }
.timeout-chip > span { display: flex; flex-direction: column; }
.timeout-chip small { font-size: 9px; font-weight: 750; text-transform: uppercase; }
.timeout-chip strong { font-variant-numeric: tabular-nums; font-size: 17px; line-height: 1.1; }
.timeout-chip.active { color: #71d6bd; border-color: rgb(113 214 189 / 38%); }
.timeout-chip.low { color: #ff8c7d; border-color: rgb(255 140 125 / 55%); }

.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.btn-worker {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #147a8a;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}
.btn-worker:hover { background: #f0f7f8; }
.btn-worker__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #147a8a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
}
.btn-worker small { display: block; color: #65736e; font-size: 11px; text-transform: uppercase; }
.btn-worker strong { display: block; color: #1a2b26; font-size: 14px; }

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }
.empty-inline { color: #8b9a95; font-size: 13px; font-style: italic; }
.error { color: #b33a3a; font-size: 12px; }

.bind-hint {
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #eaf4f5;
  border: 1px solid #bfe0d7;
  border-radius: 8px;
  color: #0f6573;
  font-size: 13px;
  font-weight: 600;
}

.shift-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.shift-toggle__label {
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
}
.shift-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: #fff;
  color: #65736e;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.shift-toggle__icon {
  display: inline-grid;
  place-items: center;
}
.shift-toggle__btn.active {
  color: #fff;
  background: #147a8a;
  border: none;
}
.shift-toggle__btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.session-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 20px;
}
.panel h3 { margin: 0 0 14px; font-size: 15px; font-weight: 700; color: #1a2b26; }
.filter-note { margin-left: 8px; font-size: 12px; font-weight: 600; color: #65736e; }

.orders-panel { margin-bottom: 16px; }
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.panel-head h3 { margin: 0; }
.order-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
}
.order-card {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.order-card:hover { border-color: #147a8a; }
.order-card.selected { border-color: #147a8a; background: #eaf4f5; }
.order-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.order-card__code {
  overflow: hidden;
  font-family: monospace;
  font-size: 14px;
  font-weight: 800;
  color: #1a2b26;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.order-card__status {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2f0;
  color: #3d4d47;
  font-size: 11px;
  font-weight: 700;
}
.order-card__status--in_progress { background: #eaf4f5; color: #0f6573; }
.order-card__status--planned { background: #fbf1dd; color: #8a5a12; }
.order-card__product { font-size: 13px; font-weight: 600; color: #1a2b26; }
.order-card__meta { font-size: 12px; color: #65736e; }

.scanner { display: flex; gap: 8px; margin-bottom: 8px; }
.scan-input {
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  font-size: 15px;
}

.lot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
}
.lot-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.lot-card:hover { border-color: #147a8a; }
.lot-card.selected { border-color: #147a8a; background: #eaf4f5; }
.lot-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.lot-card__order {
  overflow: hidden;
  font-size: 12px;
  font-weight: 800;
  color: #147a8a;
  font-family: monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lot-card__code { font-weight: 800; font-size: 15px; color: #1a2b26; font-family: monospace; }
.lot-card__product {
  font-size: 13px;
  font-weight: 600;
  color: #1a2b26;
}
.lot-card__stage {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  color: #65736e;
}
.lot-card__qty { font-size: 12px; font-weight: 600; color: #147a8a; }
.lot-card__color {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  color: #0f6573;
  background: #eaf4f5;
  font-size: 11px;
  font-weight: 700;
}

.work-panel .selected-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.work-panel .selected-header h3 { margin: 0; }
.selected-meta { margin: 4px 0 0; font-size: 13px; color: #65736e; }

.stepper-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.stepper {
  text-align: center;
  padding: 14px;
  background: #f6f8f7;
  border-radius: 10px;
}
.stepper__label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.stepper__controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.stepper__controls button {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #d4d8d5;
  background: #fff;
  font-size: 22px;
  font-weight: 700;
  color: #147a8a;
  cursor: pointer;
}
.stepper__controls button:hover { background: #eaf4f5; }
.stepper__controls strong { font-size: 24px; font-weight: 800; color: #1a2b26; min-width: 40px; }

.action-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
}
.btn-big {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  font-size: 15px;
  border-radius: 12px;
}
.btn-success { background: #1f5c4b; }
.btn-success:hover:not(:disabled) { background: #17493b; }
.btn-warn { background: #b3472d; }
.btn-warn:hover:not(:disabled) { background: #96391f; }
.btn-success, .btn-warn { color: #fff; border: none; font-weight: 700; cursor: pointer; }
.btn-success:disabled, .btn-warn:disabled { opacity: 0.5; cursor: default; }

.action-message {
  padding: 10px 14px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin: 8px 0;
}

.session-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  margin-bottom: 12px;
}
.session-status {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.session-status__label {
  font-size: 11px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
}
.session-status strong { font-size: 14px; font-weight: 800; }
.session-active { color: #1f5c4b; }
.session-paused { color: #856404; }
.session-finished { color: #65736e; }
.session-none { color: #8b9a95; }
.session-elapsed { font-size: 12px; color: #65736e; }
.session-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
/* Single action (Start, or a finished session) spans the full row; when a
   session is active/paused, Pause/Resume and Finish share the row. */
.session-actions--single {
  grid-template-columns: 1fr;
}

.btn-primary {
  display: inline-block;
  padding: 10px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #0f6573; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #147a8a;
  border: 1px solid #147a8a;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f7f8; }

.sync-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fdf6e3;
  border: 1px solid #e7d28a;
  border-radius: 10px;
}
.sync-banner--offline {
  background: #fdeeee;
  border-color: #e7b8b8;
}
.sync-banner__icon {
  font-size: 18px;
  color: #8a6d1a;
}
.sync-banner--offline .sync-banner__icon { color: #a12c2c; }
.sync-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  color: #5f5a3a;
}
.sync-banner__text strong { font-size: 13px; font-weight: 800; }
.sync-banner__text small { font-size: 12px; }
.sync-banner__action { flex-shrink: 0; }

.estimate-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #eef7f5;
  border: 1px solid #bfe0d7;
  border-radius: 10px;
  margin-top: 12px;
}
.estimate-bar__label {
  font-size: 12px;
  font-weight: 700;
  color: #1f5c4b;
  text-transform: uppercase;
}
.estimate-bar__value {
  font-size: 18px;
  font-weight: 800;
  color: #1f5c4b;
}
.estimate-bar__value small { font-size: 12px; font-weight: 600; }
.estimate-bar__hint { font-size: 12px; color: #65736e; }

.picker-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 16px;
  border-bottom: 2px solid #e2e6e3;
}
.picker-tab {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 700;
  color: #65736e;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
}
.picker-tab.active { color: #147a8a; border-bottom-color: #147a8a; }
.card-login {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}
.card-login label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
}
.card-login input {
  padding: 12px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  font-size: 16px;
  background: #fff;
}
.card-login__field { display: flex; align-items: stretch; gap: 8px; }
.card-login__field input { flex: 1; min-width: 0; }
.card-login__keypad {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  min-width: 52px;
  border: 1px solid #147a8a;
  border-radius: 10px;
  background: #fff;
  color: #147a8a;
  cursor: pointer;
}
.card-login__keypad:hover { background: #eef7f5; }
.card-login__title { margin: -4px 0 4px; color: #65736e; font-size: 14px; }
.activation-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #8a9792;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.activation-divider::before,
.activation-divider::after { height: 1px; flex: 1; background: #dfe6e3; content: ''; }

.pin-pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.pin-pad__key {
  min-height: 52px;
  font-size: 20px;
  font-weight: 700;
  color: #1a2b26;
  background: #f6f8f7;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  cursor: pointer;
}
.pin-pad__key:hover {
  border-color: #147a8a;
  background: #eaf4f5;
}

.worker-picker {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
}
.worker-picker__panel {
  width: min(640px, 92vw);
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 28px;
}
.worker-picker__panel h2 { margin: 0 0 16px; font-size: 18px; }
.worker-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.worker-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}
.worker-card:hover { border-color: #147a8a; background: #eaf4f5; }
.worker-card__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #147a8a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
}
.worker-card__name { font-weight: 700; color: #1a2b26; }
.worker-card__role { display: block; font-size: 12px; color: #65736e; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 10px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

/* Chosen-lot entry card — full modal with a back button to the lot list. */
.work-overlay {
  position: fixed;
  inset: 0;
  z-index: 45;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(15 23 20 / 55%);
}
.work-modal {
  width: min(760px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 24px;
}
.work-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}
.work-modal__headline {
  min-width: 0;
  text-align: right;
}
.work-modal__operation {
  margin: 0 0 2px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
  color: #1a2b26;
}
.work-modal__order {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 10px;
}
.work-modal__mo {
  font-size: 15px;
  font-weight: 800;
  color: #147a8a;
  font-family: monospace;
}
.work-modal__product {
  font-size: 14px;
  font-weight: 600;
  color: #5f6b6a;
}
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  color: #fff;
  background: #147a8a;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn-back:hover { background: #0f6573; }

.basket-screen {
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  border: 1px solid #dfe8e5;
  border-radius: 0;
  background: #f7fbfa;
  text-align: center;
}
.basket-screen h3 { max-width: 620px; margin: 0 0 28px; font-size: clamp(24px, 3vw, 38px); }
.basket-entry { display: flex; width: min(700px, 100%); gap: 12px; }
.basket-entry__field { display: flex; flex: 1; min-width: 0; gap: 10px; }
.basket-entry__keypad {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  min-width: 62px;
  border: 2px solid #b9cfca;
  border-radius: 14px;
  background: #fff;
  color: #147a8a;
  cursor: pointer;
}
.basket-entry__keypad:hover { border-color: #147a8a; background: #eef7f5; }
.basket-entry__keypad:focus-visible { outline: 3px solid rgb(20 122 138 / 22%); outline-offset: 2px; }
.basket-entry__input {
  flex: 1;
  min-width: 0;
  padding: 18px 20px;
  border: 2px solid #b9cfca;
  border-radius: 14px;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
}
.basket-entry__input:focus { border-color: #147a8a; outline: 3px solid rgb(20 122 138 / 18%); }
.basket-entry__submit {
  min-width: 160px;
  padding: 0 22px;
  border: 2px solid #147a8a;
  border-radius: 14px;
  color: #147a8a;
  background: #fff;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
}
.basket-entry__submit:hover { background: #eef7f5; }
.basket-entry__submit:active { background: #dcefef; }
.basket-entry__error { margin-top: 12px; font-size: 14px; }
.basket-entry__message { width: min(700px, 100%); margin: 16px 0 0; text-align: left; }

.kiosk-workspace {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
  border: 1px solid #dfe8e5;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  border-radius: 0;
  background: #fff;
}
.worker-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 12px;
  background: #123f43;
  color: #fff;
  text-align: center;
}
.worker-profile__avatar {
  display: grid;
  place-items: center;
  width: 76px;
  height: 76px;
  overflow: hidden;
  margin-bottom: 14px;
  border: 3px solid rgb(255 255 255 / 75%);
  border-radius: 50%;
  background: #2b8e94;
  font-size: 30px;
  font-weight: 800;
}
.worker-profile__avatar img { width: 100%; height: 100%; object-fit: cover; }
.worker-profile small { color: #a9d2d0; text-transform: uppercase; font-weight: 700; }
.worker-profile strong { margin-top: 6px; font-size: 18px; }
.worker-profile > span { margin: 5px 0 22px; color: #bcd7d5; font-size: 12px; }
.worker-profile .btn-secondary { margin-top: auto; border-color: #fff; color: #fff; background: transparent; }
.kiosk-task { display: flex; min-width: 0; min-height: 0; flex-direction: column; padding: 0; overflow: hidden; border-radius: 0; }
.kiosk-task__body { display: flex; flex: 1 1 auto; min-height: 0; flex-direction: column; padding: 14px 18px 8px; overflow-y: auto; }
.kiosk-task__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.kiosk-task__basket-label { color: #748680; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.kiosk-task__header .kiosk-task__basket { margin: 2px 0 0; color: #147a8a; font-family: monospace; font-size: 32px; font-weight: 850; line-height: 1; }
.kiosk-task__stage-label { padding: 10px 20px; border-radius: 999px; background: #e6f4f1; color: #1f5c4b; font-size: 17px; font-weight: 800; letter-spacing: 0.02em; line-height: 1.2; }
.kiosk-task__meta { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.kiosk-task__rate { display: inline-flex; align-items: center; gap: 6px; color: #1f5c4b; font-size: 15px; font-weight: 800; font-variant-numeric: tabular-nums; }
.kiosk-task__rate small { padding: 2px 8px; border-radius: 999px; background: #e6f4f1; color: #1f5c4b; font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.product-lines { margin-top: 14px; }
.product-lines h4 { margin: 0 0 8px; font-size: 14px; }
.product-lines__table { overflow: hidden; border: 1px solid #dfe8e5; border-radius: 0; }
.product-lines__row { display: grid; grid-template-columns: minmax(140px, 2fr) 1fr 0.6fr 0.7fr 0.7fr 0.7fr; align-items: center; gap: 8px; min-height: 44px; padding: 6px 12px; border-top: 1px solid #edf1ef; }
.product-lines__row:first-child { border-top: 0; }
.product-lines__head { min-height: 38px; background: #f3f6f5; color: #65736e; font-size: 11px; font-weight: 800; text-transform: uppercase; }
.defect-qty { text-align: right; font-variant-numeric: tabular-nums; font-weight: 800; }
.defect-qty--reject { color: #b3472d; }
.defect-qty--rework { color: #2f6ea8; }
.defect-qty--empty { color: #a6b4b0; font-weight: 600; }
.product-lines__row b { color: #147a8a; font-size: 16px; }
.basket-total {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  padding: 10px 12px;
  border: 1px solid #dfe8e5;
  border-top: 0;
  border-radius: 0;
  background: #f3f8f6;
}
.basket-total span { color: #65736e; font-size: 13px; font-weight: 700; }
.basket-total strong { color: #147a8a; font-size: 22px; }
.defect-modal { position: fixed; inset: 0; z-index: 65; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgb(15 23 20 / 60%); }
.defect-panel { display: flex; flex-direction: column; gap: 10px; width: min(640px, 96vw); max-height: min(86dvh, 760px); padding: 14px 16px; border: 1px solid #e8c3ba; border-radius: 14px; background: #fff8f5; }
.defect-panel__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.defect-panel__title { margin: 0; color: #1a2b26; font-size: 16px; font-weight: 800; }
.defect-panel__close { display: inline-flex; align-items: center; justify-content: center; width: 40px; min-width: 40px; height: 40px; border: 0; border-radius: 10px; background: #f2e6e2; color: #7a4a3e; cursor: pointer; }
.defect-panel__close:hover { background: #ecd8d2; }
.defect-panel__list { min-height: 0; overflow-y: auto; }
.defect-panel__footer { display: grid; grid-template-columns: 1fr 1.4fr; gap: 10px; }
.defect-panel__error { margin: 0; font-size: 13px; }
.defect-panel__footer .btn-big { min-height: 52px; }
.defect-row { display: grid; grid-template-columns: minmax(110px, 0.9fr) 1fr 1fr; align-items: center; gap: 10px; padding: 10px 0; border-top: 1px solid #f0d8d1; }
.defect-row:first-child { padding-top: 2px; border-top: 0; }
.defect-row__lot { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.defect-row__lot strong { color: #1a2b26; font-family: monospace; font-size: 15px; }
.defect-row__lot small { overflow: hidden; color: #65736e; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.defect-row .stepper { padding: 6px 8px; border: 1px solid #f0d8d1; border-radius: 8px; background: #fff; }
.defect-row .stepper__label { justify-content: flex-start; gap: 4px; margin-bottom: 4px; font-size: 10px; }
.defect-row .stepper__controls { gap: 4px; }
.defect-row .stepper__controls button { width: 32px; height: 32px; border-radius: 7px; font-size: 17px; }
.defect-row .stepper__controls .stepper__jump { width: 38px; font-size: 10px; letter-spacing: 0.02em; }
.defect-row .stepper__controls strong { min-width: 26px; font-size: 17px; }
.kiosk-task__error { margin-top: 12px; }
.kiosk-footer { display: grid; flex: 0 0 auto; grid-template-columns: 1.35fr 1fr 1fr; gap: 10px; margin: 0; padding: 12px 18px 16px; border-top: 1px solid #edf1ef; }
.kiosk-footer .btn-big { min-height: 64px; }

/* Touch-kiosk: portrait 15" panel or compact width -------------------------
   Big finger targets, full-width controls, panels stacked vertically. */
@media (orientation: portrait), (max-width: 1180px) {
  .terminal-header {
    grid-template-columns: 1fr auto;
    gap: 10px;
    margin: 0;
    padding: 10px 12px;
  }
  .terminal-clock strong { font-size: 22px; }
  .terminal-actions { gap: 8px; }
  .language-switch button { min-width: 44px; min-height: 46px; padding: 0 8px; }
  .timeout-chip { min-height: 54px; }
  /* Heading compacts so the work area starts higher; the worker button
     becomes a full-width tap target. */
  .page-heading {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 12px;
  }
  .page-heading h2 { font-size: 20px; }
  .btn-worker { padding: 10px 16px; }
  .btn-worker__avatar { width: 44px; height: 44px; font-size: 20px; }
  .btn-worker strong { font-size: 15px; }

  .session-grid { grid-template-columns: 1fr; gap: 12px; }
  .panel { padding: 16px; }
  .panel h3 { font-size: 15px; }

  .scanner { gap: 10px; }
  .scan-input { min-height: 52px; font-size: 16px; }
  .scanner .btn-primary { min-height: 52px; }
  .error { font-size: 13px; }

  .lot-grid { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
  .lot-card { padding: 16px; min-height: 84px; }
  .lot-card__code { font-size: 16px; }
  .lot-card__product { font-size: 15px; }
  .lot-card__stage,
  .lot-card__qty { font-size: 14px; }

  .selected-header { align-items: center; }
  .selected-meta { font-size: 14px; }

  .shift-toggle { display: flex; margin-bottom: 14px; }
  .shift-toggle__label { font-size: 13px; }
  .shift-toggle__btn {
    flex: 1;
    min-height: 52px;
    font-size: 16px;
    border-radius: 10px;
  }

  /* Three big steppers across the full-width work panel. */
  .stepper-row { grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .stepper { padding: 16px 12px; }
  .stepper__label { font-size: 13px; margin-bottom: 12px; }
  .stepper__controls { gap: 12px; }
  .stepper__controls button {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    font-size: 30px;
  }
  .stepper__controls strong { font-size: 30px; min-width: 52px; }

  .btn-big {
    min-height: 64px;
    padding: 16px 12px;
    font-size: 17px;
    border-radius: 14px;
  }
  .action-row { grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .btn-primary,
  .btn-secondary { min-height: 48px; }
  .action-message { font-size: 15px; }

  .session-bar { flex-direction: column; align-items: stretch; gap: 10px; }
  .session-status { flex-direction: row; align-items: center; gap: 10px; }
  .session-status strong { font-size: 16px; }
  .session-elapsed { font-size: 14px; }
  .session-actions { gap: 10px; }

  .estimate-bar { flex-direction: column; align-items: stretch; text-align: left; }
  .estimate-bar__value { font-size: 22px; }

  .sync-banner { flex-direction: column; align-items: stretch; gap: 8px; }

  /* Worker picker — auto-fill columns so staff fit without scrolling. */
  .worker-picker__panel { width: min(600px, 94vw); padding: 22px; }
  .worker-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
  .worker-card { padding: 14px; min-height: 76px; }
  .worker-card__avatar { width: 48px; height: 48px; font-size: 20px; }
  .worker-card__name { font-size: 15px; }
  .worker-card__role { font-size: 13px; }

  .picker-tab { padding: 12px 16px; font-size: 14px; }
  .card-login { gap: 14px; }
  .card-login label { font-size: 13px; }
  .card-login input { min-height: 52px; font-size: 18px; }
  .card-login .btn-primary { min-height: 56px; font-size: 16px; }
  .pin-pad { gap: 10px; }
  .pin-pad__key { min-height: 64px; font-size: 24px; border-radius: 12px; }

  .modal { width: min(460px, 92vw); }
  .modal input,
  .modal select { min-height: 52px; font-size: 16px; }
  .modal-actions button { min-height: 52px; font-size: 15px; }

  .work-overlay { align-items: stretch; padding: 10px; }
  .work-modal {
    width: 100%;
    max-height: calc(100dvh - 20px);
    border-radius: 12px;
    padding: 16px;
  }
  .work-modal__header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .btn-back {
    min-height: 52px;
    font-size: 16px;
    border-radius: 10px;
  }
}

/* Genuinely narrow screens: collapse the remaining multi-column grids. */
@media (max-width: 600px) {
  .terminal-header { position: relative; grid-template-columns: 1fr; }
  .terminal-clock { align-items: center; }
  .terminal-actions { justify-content: center; }
  .timeout-chip { flex: 1; justify-content: center; }
  .basket-entry { flex-direction: column; }
  .basket-entry__submit { min-height: 58px; }
  .kiosk-workspace { grid-template-columns: 1fr; }
  .worker-profile { flex-direction: row; gap: 12px; padding: 14px; text-align: left; }
  .worker-profile__avatar { width: 52px; height: 52px; margin: 0; font-size: 22px; }
  .worker-profile small { display: none; }
  .worker-profile strong { margin: 0; }
  .worker-profile > span { margin: 0; }
  .worker-profile .btn-secondary { margin: 0 0 0 auto; }
  .kiosk-task__body { padding: 12px 12px 6px; }
  .kiosk-footer { padding: 10px 12px 12px; }
  .kiosk-task__header .kiosk-task__basket { font-size: 30px; }
  .product-lines__table { overflow-x: auto; }
  .product-lines__row { min-width: 680px; }
  .defect-row, .defect-panel__footer, .kiosk-footer { grid-template-columns: 1fr; }
  .lot-grid { grid-template-columns: 1fr; }
  .stepper-row { grid-template-columns: 1fr; }
  .action-row { grid-template-columns: 1fr; }
}
</style>
